import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { generateUUID } from '../lib/session.js'

const HISTORY_KEY = 'tui.history'

function TUIOverlay({ onClose }) {
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const [history, setHistory] = useState([])
  const [cmdHistory, setCmdHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]')
    } catch {
      return []
    }
  })
  const [historyIndex, setHistoryIndex] = useState(-1)
  const inputRef = useRef(null)
  const terminalRef = useRef(null)

  // Focus trap and auto-focus
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }

    const handleTab = (e) => {
      if (e.key === 'Tab') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    window.addEventListener('keydown', handleTab)
    return () => window.removeEventListener('keydown', handleTab)
  }, [])

  // Auto-scroll to bottom on new history
  useEffect(() => {
    if (terminalRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = terminalRef.current
      if (scrollHeight - scrollTop - clientHeight < 80) {
        terminalRef.current.scrollTop = scrollHeight
      }
    }
  }, [history])

  // Arrow key history
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (historyIndex < cmdHistory.length - 1) {
        const newIndex = historyIndex + 1
        setHistoryIndex(newIndex)
        setInput(cmdHistory[cmdHistory.length - 1 - newIndex] || '')
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1
        setHistoryIndex(newIndex)
        setInput(cmdHistory[cmdHistory.length - 1 - newIndex] || '')
      } else if (historyIndex === 0) {
        setHistoryIndex(-1)
        setInput('')
      }
    }
  }

  const addToHistory = (cmd) => {
    const updated = [...cmdHistory, cmd]
    if (updated.length > 50) updated.shift()
    setCmdHistory(updated)
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(updated))
    } catch {
      // ignore
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const cmd = input.trim()
    if (!cmd) return

    addToHistory(cmd)
    setHistoryIndex(-1)

    setHistory((h) => [...h, { id: generateUUID(), type: 'prompt', text: cmd }])

    setTimeout(() => {
      let response
      const parts = cmd.split(' ')
      const base = parts[0].toLowerCase()

      switch (base) {
        case 'help':
        case 'man':
          response = 'Available commands: ls, goto <section>, open post "<title>", whoami, man, clear, neofetch, htop, docker ps, ask "<question>", exit'
          break
        case 'ls':
          response = 'bio  work  education  projects  skills  writing  contact'
          break
        case 'whoami':
          response = 'visitor - guest user on mugunth.dev'
          break
        case 'clear':
          setHistory([{ id: generateUUID(), type: 'system', text: 'Terminal cleared.' }])
          setInput('')
          return
        case 'goto': {
          const section = parts[1]
          const routes = {
            bio: '/',
            skills: '/skills',
            education: '/education',
            work: '/experience',
            writing: '/blog',
            projects: '/projects',
            contact: '/contact',
            resume: '/resume',
            stats: '/stats',
          }
          if (routes[section]) {
            navigate(routes[section])
            response = `Navigating to ${section}…`
            setTimeout(onClose, 300)
          } else {
            response = `Unknown section: ${section}. Try: bio, work, education, projects, skills, writing, contact`
          }
          break
        }
        case 'open': {
          if (cmd.includes('post')) {
            const title = cmd.match(/"([^"]+)"/)?.[1]
            if (title) {
              const slug = title.toLowerCase().replace(/\s+/g, '-')
              navigate(`/blog/${slug}`)
              response = `Opening post "${title}"…`
              setTimeout(onClose, 300)
            } else {
              response = 'Usage: open post "<title>"'
            }
          } else {
            response = 'Usage: open post "<title>"'
          }
          break
        }
        case 'neofetch':
          response = 'OS: mugunth.dev\nKernel: React 19 + Vite\nShell: TUI v0.1\nWM: Chrome\nTheme: Soft Paper'
          break
        case 'htop':
          response = 'PID  USER   CPU%  MEM%  COMMAND\n1    root    0.0   2.1  django\n42   agent   12.3  8.4  claude-api\n1337 visitor  0.0   0.1  tui-shell'
          break
        case 'docker':
          if (parts[1] === 'ps') {
            response = 'CONTAINER ID  IMAGE              STATUS\nabc123        aboutmecosmic:web  Up 3 days\ndef456        neo4j:latest     Up 3 days\nghi789        django:app        Up 3 days'
          } else {
            response = 'Usage: docker ps'
          }
          break
        case 'ask': {
          const question = cmd.match(/"([^"]+)"/)?.[1]
          if (question) {
            response = `Queued: "${question}" - the agent will reply in the chat panel.`
          } else {
            response = 'Usage: ask "<question>"'
          }
          break
        }
        case 'exit':
        case 'quit':
          response = 'exit: nice try. The terminal does not close.'
          break
        // Easter eggs
        case 'sudo':
          if (cmd.includes('hire mugunth')) {
            response = 'Permission granted. Forwarding to contact form…'
            navigate('/contact')
            setTimeout(onClose, 500)
          } else {
            response = 'sudo: unknown command'
          }
          break
        case 'rm':
          if (cmd.includes('-rf boredom')) {
            response = 'boredom: removed. Enjoy the site.'
          } else {
            response = 'rm: refusing to delete that. Try "rm -rf boredom"'
          }
          break
        case 'git': {
          if (cmd.includes('blame mugunth')) {
            response = 'a4331ac - feat: rewrite chat panel to spec - Mugunth J. Chandirasekaran'
          } else if (cmd.includes('log --oneline')) {
            response = 'a4331ac feat: rewrite chat panel\n05af795 ui: centered TUI\n7b6e032 fix: align API fields\n...'
          } else {
            response = 'git: unknown subcommand'
          }
          break
        }
        case 'curl':
          if (cmd.includes('wtf.is/mugunth')) {
            response = 'HTTP/2 200\nContent-Type: application/json\n\n{"name": "Mugunth J. Chandirasekaran", "status": "building"}'
          } else {
            response = 'curl: could not resolve host'
          }
          break
        default:
          response = `Command not found: ${base}. Type "help" for available commands.`
      }

      setHistory((h) => [...h, { id: generateUUID(), type: 'output', text: response }])
    }, 150)

    setInput('')
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid" role="dialog" aria-modal="true">
      <div
        ref={terminalRef}
        className="place-self-center w-full h-[92vh] sm:w-[600px] sm:max-w-[90vw] sm:h-[400px] sm:max-h-[70vh] bg-tui-bg text-tui-text font-mono text-[13px] leading-[1.65] flex flex-col overflow-y-auto shadow-2xl"
      >
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 shrink-0">
          <span className="text-[11.5px] text-tui-dim">mugunth.dev - tty</span>
          <button
            onClick={onClose}
            className="text-[11.5px] text-tui-dim hover:text-tui-text cursor-pointer"
          >
            ⌘+I to dismiss
          </button>
        </div>

        {/* Input */}
        <div className="px-5 pt-2 text-tui-dim text-[11.5px]">mugunthjc.dev terminal v0.1 - type "help" for available commands.</div>

        {/* Body */}
        {history.length > 0 && (
          <div className="p-5 space-y-1">
            {history.map((entry) => {
              if (entry.type === 'prompt') {
                return (
                  <div key={entry.id} className="flex items-start gap-1">
                    <span className="text-[#9dbf9f] shrink-0">visitor@mugunth.dev</span>
                    <span className="text-[#888]">:</span>
                    <span className="text-[#8aaecf]">~</span>
                    <span className="text-[#888]">$</span>
                    <span className="text-[#dcdcdc] ml-1">{entry.text}</span>
                  </div>
                )
              }
              if (entry.type === 'output') {
                return (
                  <div key={entry.id} className="text-[#bfbfbf] whitespace-pre-wrap pl-0">
                    {entry.text}
                  </div>
                )
              }
              return (
                <div key={entry.id} className="text-tui-dim">
                  {entry.text}
                </div>
              )
            })}
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-5 py-2 flex items-center gap-1.5 shrink-0">
          <span className="text-[#9dbf9f] shrink-0">visitor@mugunth.dev</span>
          <span className="text-[#888]">:</span>
          <span className="text-[#8aaecf]">~</span>
          <span className="text-[#888]">$</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent outline-none text-[#dcdcdc] font-mono ml-1"
            autoFocus
          />
        </form>
      </div>
    </div>
  )
}

export default TUIOverlay
